import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  appendListVehicleTracking,
  setGetListLoading,
  setListVehicleTracking
} from 'src/features/vehicleSlice';
import { STATUS_API } from 'src/app/constant/config';
import { deduplicatedFetch } from 'src/app/utils/requestDeduplicator';

const DEFAULT_PAGE_SIZE = 300;
const DEFAULT_MAX_PARALLEL = 5;

const INITIAL_PROGRESS = {
  loading: false, // đang chờ trang 1 (chưa có gì để hiển thị)
  loadingMore: false, // đã hiển thị trang 1, các trang sau đang chảy về nền
  loaded: 0,
  total: 0
};

/**
 * Nạp danh sách xe tracking theo từng trang thay vì 1 request page_size lớn.
 * Trang 1 ghi đè listVehicleTracking để UI có dữ liệu ngay; các trang sau gọi nền
 * (tối đa maxParallel request đồng thời) và append dần vào store.
 *
 * buildUrl(page, pageSize) -> URL của trang. API cần trả payload.latest_periodics và
 * payload.total; không có total thì coi như chỉ 1 trang.
 *
 * Mỗi lần gọi load() mới (hoặc unmount) sẽ huỷ lượt nạp cũ, tránh ghi đè dữ liệu mới.
 */
export default function useProgressiveVehicleTracking({
  pageSize = DEFAULT_PAGE_SIZE,
  maxParallel = DEFAULT_MAX_PARALLEL
} = {}) {
  const dispatch = useDispatch();
  const tokenRef = useRef(0);
  const [progress, setProgress] = useState(INITIAL_PROGRESS);

  useEffect(
    () => () => {
      tokenRef.current += 1;
    },
    []
  );

  const load = useCallback(
    async buildUrl => {
      const token = tokenRef.current + 1;
      tokenRef.current = token;
      const isStale = () => token !== tokenRef.current;

      setProgress({ ...INITIAL_PROGRESS, loading: true });
      dispatch(setGetListLoading(STATUS_API.PENDING));

      let firstPayload;
      try {
        const firstRes = await deduplicatedFetch(buildUrl(1, pageSize));
        firstPayload = firstRes?.data?.payload || {};
      } catch (error) {
        if (isStale()) return;
        dispatch(setListVehicleTracking([]));
        dispatch(setGetListLoading(STATUS_API.ERROR));
        setProgress(INITIAL_PROGRESS);
        return;
      }
      if (isStale()) return;

      const firstRows = firstPayload.latest_periodics || [];
      const total = Number(firstPayload.total) || firstRows.length;
      const totalPages = Math.ceil(total / pageSize);

      dispatch(setListVehicleTracking(firstRows));
      dispatch(setGetListLoading(STATUS_API.SUCCESS));

      let loaded = firstRows.length;
      setProgress({
        loading: false,
        loadingMore: totalPages > 1,
        loaded,
        total
      });
      if (totalPages <= 1) return;

      const remaining = [];
      for (let page = 2; page <= totalPages; page += 1) remaining.push(page);

      while (remaining.length > 0) {
        if (isStale()) return;
        const batch = remaining.splice(0, maxParallel);
        const responses = await Promise.allSettled(
          batch.map(page => deduplicatedFetch(buildUrl(page, pageSize)))
        );
        if (isStale()) return;

        const rows = [];
        responses.forEach(res => {
          if (res.status !== 'fulfilled') return;
          rows.push(...(res.value?.data?.payload?.latest_periodics || []));
        });
        if (rows.length > 0) dispatch(appendListVehicleTracking(rows));

        loaded += rows.length;
        const loadedSoFar = Math.min(loaded, total);
        setProgress(prev => ({ ...prev, loaded: loadedSoFar }));
      }

      if (!isStale()) setProgress(prev => ({ ...prev, loadingMore: false }));
    },
    [dispatch, pageSize, maxParallel]
  );

  return { load, progress };
}
